import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import { useDocuments } from '../contexts/DocumentContext';
import { useAuth } from '../contexts/AuthContext';
import { generateDocumentId } from '../utils/idGenerator';
import config from '../config';
import './UploadPage.css';

const UploadPage = () => {
  const navigate = useNavigate();
  const { createDocument } = useDocuments();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;
    
    // Check if file type is allowed
    if (!config.allowedFileTypes.includes(selectedFile.type)) {
      setError('Please upload a PDF file');
      setFile(null);
      return;
    }
    
    // Check file size
    if (selectedFile.size > config.maxFileSize) {
      setError(`File size exceeds ${config.maxFileSize / (1024 * 1024)}MB limit`);
      setFile(null);
      return;
    }
    
    setError(null);
    setFile(selectedFile);
    
    // Set document name from file name (remove extension)
    const fileName = selectedFile.name.replace(/\.[^/.]+$/, '');
    setDocumentName(fileName);
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    
    try {
      setLoading(true);
      
      // Convert file to base64 for storage
      const base64File = await convertFileToBase64(file);
      
      // Create document in context
      const newDocument = await createDocument({
        name: documentName || 'Untitled Document',
        fileData: base64File,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        fields: [],
        status: 'draft',
        // We'll determine signature type later based on recipients
        signatureType: null
      });
      
      console.log('Document created successfully:', newDocument);
      
      // Go directly to recipient management
      navigate(`/editor/${newDocument.id}`, { 
        state: { 
          initialStep: 'recipients' 
        } 
      });
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Failed to upload document. Please try again.');
      setLoading(false);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  return (
    <div className="upload-page">
      <div className="container">
        <h1 className="page-title">Upload Document</h1>
        <p className="page-subtitle">Upload a PDF document to start the signing process</p>
        
        {error && (
          <Alert 
            type="error"
            title="Error"
            message={error} 
            dismissible={true} 
            onDismiss={() => setError(null)}
            icon={null}
            action={null}
          />
        )}
        
        <Card 
          className="upload-card"
          title=""
          subtitle=""
          footer=""
        >
          <form onSubmit={handleContinue}>
          <div 
            className={`upload-area ${dragActive ? 'upload-area-active' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleFileChange(e.target.files[0])}
              accept=".pdf"
              className="upload-input"
            />
            
            <div className="upload-content">
              <div className="upload-icon">📄</div>
              <h3 className="upload-title">
                {file ? file.name : 'Drag & Drop your PDF here'}
              </h3>
              <p className="upload-subtitle">
                {file 
                  ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                  : 'or click to browse files (PDF only, max 10MB)'
                }
              </p>
              
              {!file && (
                <Button 
                  onClick={handleButtonClick}
                  variant="secondary"
                  className="mt-md"
                >
                  Select File
                </Button>
              )}
              
              {file && (
                <Button 
                  onClick={() => setFile(null)}
                  variant="tertiary"
                  size="small"
                  className="mt-sm"
                >
                  Remove File
                </Button>
              )}
            </div>
          </div>
          
          {file && (
            <div className="document-name-section mt-md">
              <label htmlFor="documentName" className="form-label">
                Document Name
              </label>
              <input
                type="text"
                id="documentName"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="Enter document name"
                className="form-control"
              />
            </div>
          )}
          
          
          <div className="upload-actions mt-lg">
            <Button 
              type="submit" 
              disabled={!file || loading}
              loading={loading}
              fullWidth
            >
              Continue to Recipients
            </Button>
          </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default UploadPage;
